import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { connect, PromiseState } from 'react-refetch';
import { useSelector } from 'react-redux';

import getArtistConcerts, {
  IArtistConcertsUrlArgs,
} from 'url-constructors/getArtistConcerts';
import getTableHeaders from './utils/getTableHeaders';
import useSorting from 'components/data-containers/BrowseEntities/shared/useSorting';
import useOffset from 'hooks/useOffset';
import useMedia from 'hooks/useMedia';
import getMenuItems from './utils/getMenuItems';
import getMobileItems from './utils/getMobileItems';

import { EntityContext } from 'context/entityContext';
import { RootState } from 'redux/rootReducer';

import GenericComponent from 'components/utility/GenericComponent/GenericComponent';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';
import Layout from 'components/styled/Layout/Layout';
import Placeholder from 'components/data-visualization/Table/Placeholder';
import Table from './Table';
import List from './List';
import Pagination from 'components/data-visualization/Table/Pagination';

import { GenericComponentWrapper } from './styles';

import { IConcertsProps, IOuterProps } from './types';

const withChangeMetric = new Set(['date', 'tourName', 'venue']);
const notSortable = new Set(['country', 'city']);

const dataSources = (): any => ({
  fetchConcerts: (props: IArtistConcertsUrlArgs): { concertsData: string } => ({
    concertsData: getArtistConcerts(props),
  }),
});

const Concerts: React.FC<IConcertsProps> = (props) => {
  const {
    concertsData = PromiseState.create(),
    fetchConcerts,
    dateStatus,
  } = props;

  const { pending, reason, rejected, value } = concertsData;
  /**
   * Variables from context.
   */
  const { entityIds, filters, customRegionFilter } = useContext(EntityContext);
  /**
   * Media query to switch between mobile/desktop behavior
   */
  const isLargeScreen = useMedia('(min-width: 60em)');

  const defaultSortMetric = {
    value: 'date',
    label: 'date',
    key: 'date',
    sortOrder: dateStatus === 'past' ? 'desc' : 'asc',
  };

  const tableHeaders = getTableHeaders();
  const tableHeadItems = getMenuItems(dateStatus);
  const mobileItems = getMobileItems(dateStatus);
  const countries = useSelector((state: RootState) => state.countries.data);
  const {
    sortOrder,
    setSortOrder,
    sortMetric,
    setSortMetric,
    changeSortMetric,
    switchSortOrder,
  } = useSorting(defaultSortMetric, dateStatus === 'past' ? 'desc' : 'asc');

  /**
   * Use limit and offset for the API data
   */
  const {
    offsetState,
    setOffset,
    setEntities,
    setInvalidate,
    setState,
  } = useOffset({
    limit: 5,
    increment: 10,
    entitiesKey: 'concerts',
  });
  const { limit, increment, offset, value: concerts } = offsetState;

  /*
   * When an interaction took place that will trigger a new API call and not a
   * limit/offset change API call, we want to clear the data in state.
   */
  useEffect(() => {
    // `setInvalidate` flushes the entities when the next batch of entities arrive.
    // Happens at the next `setEntities` so we don't see a skeleton loader in between.
    setInvalidate();
  }, [setInvalidate, sortMetric, sortOrder, filters]);

  useEffect(() => {
    fetchConcerts({
      dateStatus,
      entityId: entityIds[0],
      region: filters?.region,
      countryIds: filters?.country,
      limit,
      offset,
      sortBy: sortMetric?.value,
      sortOrder,
      customRegion: customRegionFilter,
    });
  }, [
    dateStatus,
    entityIds,
    fetchConcerts,
    filters,
    offset,
    limit,
    sortOrder,
    sortMetric,
    customRegionFilter,
  ]);

  /*
   * Pass the concert data to the `useOffset` hook.
   */
  useEffect(() => {
    if (value) {
      setEntities(value);
    }
  }, [value, setEntities]);

  const setInfiniteOffset = useCallback(() => {
    if (!offset) {
      setState({ limit: 10, offset: 5 });
    } else {
      setOffset(10);
    }
  }, [setState, setOffset, offset]);

  /**
   * True if there are no data at all.
   * @returns {boolean}
   */
  const hasNoData = useMemo(() => {
    if (Array.isArray(concerts?.concerts)) {
      return concerts?.concerts.length === 0;
    }

    return false;
  }, [concerts]);

  return (
    <GenericComponentWrapper>
      <GenericComponent
        title={`${dateStatus} Concerts`}
        loading={pending}
        showToolbarDropdown={false}
        allowedFilters={['country', 'region', 'customRegion']}
        isWide
      >
        <ErrorBoundary
          error={rejected ? new Error(reason) : null}
          errorInfo={reason}
        >
          <AnimatedLoadingWrapper loading={pending}>
            {hasNoData ? (
              <Layout.Relative>
                <Placeholder
                  type="concert"
                  entityType="artist"
                  isSmallScreen={!isLargeScreen}
                />
              </Layout.Relative>
            ) : (
              <>
                {isLargeScreen ? (
                  <Table
                    changeSortMetric={changeSortMetric}
                    concerts={concerts?.concerts}
                    countries={countries}
                    notSortable={notSortable}
                    sortMetric={sortMetric}
                    sortOrder={sortOrder}
                    setSortMetric={setSortMetric}
                    setSortOrder={setSortOrder}
                    tableHeaders={tableHeaders}
                    tableHeadItems={tableHeadItems}
                    tableName="concerts"
                    withChangeMetric={withChangeMetric}
                  />
                ) : (
                  <List
                    changeSortMetric={changeSortMetric}
                    concerts={concerts?.concerts}
                    countries={countries}
                    isLargeScreen={isLargeScreen}
                    mobileItems={mobileItems}
                    sortMetric={sortMetric}
                    sortOrder={sortOrder}
                    switchSortOrder={switchSortOrder}
                  />
                )}

                {concerts?.concerts && concerts?.concerts.length > 0 && (
                  <Pagination
                    current={concerts?.concerts.length}
                    total={value?.totalConcertCount}
                    offset={increment}
                    hasMoreResults={
                      concerts?.concerts.length < value?.totalConcertCount
                    }
                    onClick={setInfiniteOffset}
                    type={`${dateStatus} concerts`}
                    additionalInfo={`Data Source: Songkick`}
                  />
                )}
              </>
            )}
          </AnimatedLoadingWrapper>
        </ErrorBoundary>
      </GenericComponent>
    </GenericComponentWrapper>
  );
};

export default connect<IOuterProps>(dataSources)(Concerts as any);
